import { isAlreadyLinked } from 'shared/domain/issue/linkedDocumentation';
import { ReactElement, useCallback, useMemo } from 'react';
import { CommonTranslationKeys } from 'intl/keys';
import { linkedToDocumentationToastKey } from 'intl/helpers/documentation';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { useCurrentIssue } from 'components/dataProviders/withIssue';
import { useProjectId } from 'store/project';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { LinkTargetChooser } from './LinkTargetChooser';
import { isDocumentationLinked, listLinkableVersions, toLinkTarget } from './model';
import { useLinkTargetChoice } from './useLinkTargetChoice';
import { useImportFromDocumentation } from './ImportFromDocumentationContext';
import { useImportFromDocumentationBrowser } from './useImportFromDocumentationBrowser';
import { DocumentationBrowserDialog } from 'components/documentation/DocumentationBrowser';
import { useIssueDocumentationOperations } from 'store/issue';
import { showErrorToastAndLog, showToast } from 'services/toaster';

export function ImportFromDocumentationDialog(): ReactElement {
  const issue = useCurrentIssue();
  const selectedProjectId = useProjectId();
  const { issueId, projectId } = useIssueRouteParams();

  const { isOpen, closeImportDialog } = useImportFromDocumentation();
  const browser = useImportFromDocumentationBrowser(isOpen);
  const { importFromDocumentation, importPending } =
    useIssueDocumentationOperations();

  const resolvedProjectId = projectId ?? selectedProjectId;
  const resolvedIssueId = issueId ?? issue?._id;

  const linkedDocumentation = useMemo(() => issue?.linkedDocumentation ?? [], [issue?.linkedDocumentation]);
  const selectedDocumentation = browser.selectedDocumentation;
  const { choice, setChoice } = useLinkTargetChoice(isOpen, selectedDocumentation, linkedDocumentation);

  const linkableVersions = useMemo(
    () => (selectedDocumentation ? listLinkableVersions(selectedDocumentation, linkedDocumentation) : []),
    [selectedDocumentation, linkedDocumentation]
  );
  const isSelectedDocumentationLinked = useMemo(
    () => (selectedDocumentation ? isDocumentationLinked(selectedDocumentation, linkedDocumentation) : false),
    [selectedDocumentation, linkedDocumentation]
  );

  const selectedImportCandidate = useMemo(
    () => (selectedDocumentation?._id && choice ? toLinkTarget(selectedDocumentation, choice) : undefined),
    [selectedDocumentation, choice]
  );

  const handleImport = useCallback(() => {
    if (!issue || !resolvedProjectId || !resolvedIssueId) return;

    if (!selectedImportCandidate) {
      closeImportDialog();
      return;
    }

    importFromDocumentation({
      issue,
      issueId: resolvedIssueId,
      target: selectedImportCandidate,
    })
      .then(() => {
        showToast({
          message: { id: linkedToDocumentationToastKey(selectedImportCandidate) },
          type: TOASTER_TYPES.SUCCESS,
        });
      })
      .catch((err) => {
        showErrorToastAndLog('Import from documentation failed',
          err
        );
      })
      .finally(() => {
        closeImportDialog();
      });
  }, [
    issue,
    resolvedProjectId,
    resolvedIssueId,
    selectedImportCandidate,
    closeImportDialog,
    importFromDocumentation,
  ]);

  const isImportDisabled =
    browser.loading ||
    Boolean(browser.loadError) ||
    !selectedImportCandidate ||
    isAlreadyLinked(linkedDocumentation, selectedImportCandidate) ||
    !resolvedIssueId ||
    !resolvedProjectId;

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={closeImportDialog}
      titleFallbackId={CommonTranslationKeys.chooseFromDocumentation}
      searchDataQa='import-from-documentation-search'
      browser={browser}
      options={
        selectedDocumentation?._id && choice ? (
          <LinkTargetChooser
            versions={linkableVersions}
            isDocumentationLinked={isSelectedDocumentationLinked}
            choice={choice}
            onChange={setChoice}
          />
        ) : undefined
      }
      confirmLabelId={CommonTranslationKeys.import}
      onConfirm={handleImport}
      confirmDisabled={isImportDisabled}
      confirmLoading={importPending || browser.loading}
    />
  );
}
